// Shell-wide banners rendered under the header on every surface: the
// multi-tab storage notice and the one-time passkey nudge. Both wrap on
// narrow viewports (the buttons drop to their own row) instead of truncating.

import { KeyRound } from "lucide-react";
import { useState } from "react";

import { useAuth } from "../auth";
import type { ShellStorageState } from "../core/shell-types";

// Multi-tab notice: the worker is on memory storage because another tab holds
// the durable pool ("locked"), or this tab held it and lost it ("evicted" —
// its core is stopped until it takes the pool back). "Use this tab" steals
// the lock and re-opens the account here.
export function StorageBanner({
  storage,
  onUseThisTab,
}: {
  storage: ShellStorageState | null;
  onUseThisTab: () => Promise<unknown>;
}) {
  const [busy, setBusy] = useState(false);
  if (!storage || storage.storage !== "memory") {
    return null;
  }
  const message =
    storage.reason === "locked"
      ? "Lately is open in another tab — this tab won’t keep mail offline."
      : storage.reason === "evicted"
        ? "Lately moved to another tab."
        : storage.reason === "unsupported"
          ? "This browser can’t keep mail offline — Lately runs in memory only."
          : null;
  if (!message) {
    return null;
  }
  const canTakeOver = storage.reason === "locked" || storage.reason === "evicted";
  return (
    <output
      data-storage={storage.storage}
      data-storage-reason={storage.reason}
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm text-foreground"
    >
      <span className="min-w-0 flex-1 basis-48">{message}</span>
      {canTakeOver ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            onUseThisTab()
              .catch(() => undefined)
              .finally(() => setBusy(false));
          }}
          className="shrink-0 rounded-md border border-amber-500/50 px-2 py-0.5 text-xs font-medium transition-colors hover:bg-amber-500/20 disabled:opacity-50"
        >
          {busy ? "Switching…" : "Use this tab"}
        </button>
      ) : null}
    </output>
  );
}

const PASSKEY_BANNER_DISMISSED_KEY = "lately.web.vault.passkeyBannerDismissed";

// One-time nudge after the first sign-in: the vault exists but only this
// browser can unlock it; a passkey makes it survive Lock. The passkey travels
// to the user's other devices via the platform password manager (the vault's
// KEY, not its data: each surface still signs in once and then reuses it).
export function PasskeyBanner() {
  const { vault, enrollPasskey } = useAuth();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(PASSKEY_BANNER_DISMISSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  if (
    dismissed ||
    vault.kind !== "ready" ||
    vault.protection !== "browser" ||
    !vault.passkeyAvailable
  ) {
    return null;
  }
  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(PASSKEY_BANNER_DISMISSED_KEY, "1");
    } catch {
      // preference only
    }
  };
  return (
    <div
      data-vault-banner="passkey"
      className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-border bg-muted/40 px-4 py-2 text-sm"
    >
      <KeyRound aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 basis-56">
        {note ??
          "Protect Lately with a passkey: one Touch ID / Face ID unlocks your mail here after a lock. If you already made one on another device, it is used as is."}
      </span>
      <span className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            setNote(null);
            enrollPasskey()
              .then(() => dismiss())
              .catch((cause: unknown) =>
                setNote(cause instanceof Error ? cause.message : String(cause)),
              )
              .finally(() => setBusy(false));
          }}
          className="rounded-full bg-primary px-3 py-0.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {busy ? "Waiting…" : "Use a passkey"}
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="rounded-full px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          Not now
        </button>
      </span>
    </div>
  );
}
